import { authService } from '@/services/authService';
import {
    EyeIcon,
    EyeSlashIcon,
    LockClosedIcon,
} from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { z } from 'zod';

const schema = z
  .object({
    password: z
      .string()
      .min(12, 'Password must be at least 12 characters')
      .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Must contain at least one lowercase letter')
      .regex(/\d/, 'Must contain at least one number')
      .regex(
        /[!@#$%^&*(),.?":{}|<>]/,
        'Must contain at least one special character'
      ),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--primary-600) 0%,
    var(--primary-800) 100%
  );
  padding: 1rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: var(--shadow-xl);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    color: var(--primary-600);
    font-size: 2rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
    letter-spacing: 2px;
  }

  p {
    color: var(--gray-600);
    font-size: 0.875rem;
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  position: relative;

  label {
    display: block;
    font-weight: 500;
    color: var(--gray-700);
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }

  input {
    width: 100%;
    padding: 0.625rem 0.75rem;
    padding-left: 2.5rem;
    border: 1px solid var(--gray-300);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
    }

    &::placeholder {
      color: var(--gray-400);
    }
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 2.25rem;
  color: var(--gray-400);
  width: 1.25rem;
  height: 1.25rem;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 2.25rem;
  background: none;
  border: none;
  color: var(--gray-400);
  cursor: pointer;
  padding: 0;
  width: 1.25rem;
  height: 1.25rem;

  &:hover {
    color: var(--gray-600);
  }

  svg {
    width: 100%;
    height: 100%;
  }
`;

const ErrorMessage = styled.p`
  color: var(--error-600);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const ErrorBox = styled.div`
  background-color: var(--error-50);
  border-radius: 0.5rem;
  padding: 0.75rem;

  p {
    color: var(--error-800);
    font-size: 0.875rem;
    margin: 0;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1rem;
  background: var(--primary-600);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BackLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.875rem;
  color: var(--gray-600);

  a {
    color: var(--primary-600);
    font-weight: 500;

    &:hover {
      color: var(--primary-700);
    }
  }
`;

const InvalidToken = styled.div`
  text-align: center;
  padding: 1rem 0;

  p {
    color: var(--error-700);
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }
`;

const ResetPassword: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    if (!token) return;
    try {
      await authService.resetPassword(token, data.password);
      navigate('/login', {
        state: { message: 'Password reset successfully. You can now sign in.' },
      });
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { error?: string } };
      };
      const msg =
        err.response?.data?.error || 'Something went wrong. Please try again.';
      setError('root', { message: msg });
    }
  };

  if (!token) {
    return (
      <Container>
        <Card>
          <Logo>
            <h1>MARKETEER</h1>
            <p>Reset your password</p>
          </Logo>
          <InvalidToken>
            <p>This reset link is invalid or has expired.</p>
            <BackLink>
              <Link to="/forgot-password">Request a new reset link</Link>
            </BackLink>
          </InvalidToken>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Logo>
          <h1>MARKETEER</h1>
          <p>Choose a new password</p>
        </Logo>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <label htmlFor="password">New password</label>
            <Icon>
              <LockClosedIcon />
            </Icon>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="At least 12 characters"
              {...register('password')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
            </PasswordToggle>
            {errors.password && (
              <ErrorMessage>{errors.password.message}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <label htmlFor="confirmPassword">Confirm new password</label>
            <Icon>
              <LockClosedIcon />
            </Icon>
            <input
              id="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repeat your password"
              {...register('confirmPassword')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
            >
              {showConfirm ? <EyeSlashIcon /> : <EyeIcon />}
            </PasswordToggle>
            {errors.confirmPassword && (
              <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>
            )}
          </FormGroup>

          {errors.root && (
            <ErrorBox>
              <p>{errors.root.message}</p>
            </ErrorBox>
          )}

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Resetting...' : 'Reset password'}
          </SubmitButton>
        </Form>

        <BackLink>
          <Link to="/login">Back to sign in</Link>
        </BackLink>
      </Card>
    </Container>
  );
};

export default ResetPassword;
